import { MapBounds } from '../types';

/**
 * Søkeområde for «Søk i dette området».
 *
 * Hvorfor en sirkel med tak, og ikke bare utsnittet: en viewport-bbox har ingen
 * øvre grense. Zoomer brukeren ut, vokser området fritt, og det er nettopp derfor
 * lav zoom var dyrt (målt: 3000 dokumenter lest, 144 innenfor utsnittet). En fast
 * maks-radius setter et HARDT tak på hvor mange dokumenter én henting kan røre,
 * uansett zoom.
 *
 * Sirkelen tegnes også på kartet, slik at brukeren ser presis hvilket område som
 * ble søkt — i stedet for å måtte gjette hvorfor det ikke er markører lenger ute.
 */

/**
 * Radien FØLGER UTSNITTET, med et hardt tak.
 *
 * Den var en periode fast på 25 km, uavhengig av zoom (df708df, 15.08.2026). Det
 * ga en tom app: `runSearch` henter bbox-en rundt sirkelen, så ved lav zoom lastet
 * vi 25 km midt i et utsnitt på flere hundre km, og resten av kartet var tomt.
 * Verre: `needsNewSearch` sluttet samtidig å se på zoom, så det tomme området ga
 * ingen «Søk i dette området»-knapp heller. Brukeren satt igjen uten markører og
 * uten noen måte å be om dem. Rapportert på Android 16.08.2026.
 *
 * Fast radius ble innført for å hindre at søkesirkelen dukket opp av seg selv når
 * brukeren zoomet. Men DEN feilen lå i porten, ikke i radien: porten brukte
 * «zoom uendret» som en gjetning på om brukeren flyttet kartet. `utils/mapMoveOrigin`
 * (samme commit) erstattet gjetningen med at appen sier fra om sine egne flytt, og
 * dermed trengs ikke fast radius for å holde sirkelen i ro.
 *
 * 50 km er realistisk «i nærheten» for en bobil og holder bbox-en innenfor ~12
 * geoceller på 0,5° selv på nordiske breddegrader. Målt verste 50 km-boks
 * 15.08.2026: Bohuslän 9 134 dokumenter — under AREA_SAFETY_CAP i getCachedSpotsInArea.
 */
export const MAX_SEARCH_RADIUS_KM = 50;

/**
 * Minste NYTTIGE radius — ikke bare «minste som er synlig å tegne».
 *
 * Ren zoom-skalering (`viewKm * SEARCH_BUFFER`) gir ubrukelig små søk ved høy
 * zoom: på zoom 15 er utsnittet ~1,5 km, og et søk ville hentet ~2 km. I utmark
 * er det tomt, og brukeren trykker «Søk i dette området» og får ingen treff.
 * Rapportert 16.08.2026, rett etter at zoom-skaleringen ble tatt tilbake.
 *
 * Gulvet er det fast-radius-designet hadde rett i: «søk her» skal bety «alt i
 * nærheten», og for en bobil er det titalls kilometer — ikke det som tilfeldigvis
 * er innenfor skjermkanten. 25 km er målt: 238–964 plasser innenfor radien.
 */
export const MIN_SEARCH_RADIUS_KM = 25;

/**
 * Vi søker litt VIDERE enn brukeren ser.
 *
 * Uten slakk omskriver sirkelen utsnittet eksakt, og da faller et hjørne utenfor
 * ved den minste panorering — knappen ville blinket fram med én gang.
 */
export const SEARCH_BUFFER = 1.4;

/** Hvor langt sentrum må flytte seg, som andel av RADIEN vi hentet, før vi tilbyr
 *  nytt søk. Med gulvet på 25 km er terskelen minst 12,5 km — langt nok til at
 *  knappen ikke blafrer, men innenfor det vi faktisk har data for. */
export const RESEARCH_MOVE_FRACTION = 0.5;

/** Hvor mye større en NY søkeradius må være enn den vi allerede har, før det er
 *  verdt å tilby et nytt søk. Under dette ville knappen hentet nesten det samme. */
export const RESEARCH_GROWTH_FACTOR = 1.15;

/**
 * Radien et gitt utsnitt ville gitt. Delt ut som egen funksjon fordi
 * `needsNewSearch` må kunne spørre «ville et nytt søk her faktisk gitt meg mer?»
 * uten å bygge en hel sirkel.
 */
export const radiusForViewKm = (viewKm: number): number =>
  Math.min(MAX_SEARCH_RADIUS_KM, Math.max(MIN_SEARCH_RADIUS_KM, viewKm * SEARCH_BUFFER));

const EARTH_RADIUS_KM = 6371;
const toRad = (d: number) => (d * Math.PI) / 180;

export interface SearchCircle {
  lat: number;
  lon: number;
  /** Radien vi HENTET innenfor. Kappet ved MAX_SEARCH_RADIUS_KM. */
  radiusKm: number;
  /**
   * Hvor stort utsnittet var da vi søkte (avstand sentrum → fjerneste hjørne).
   *
   * Terskelen for å tilby nytt søk måles mot DENNE, ikke mot `radiusKm`. Det er
   * dét som gjør terskelen zoom-stabil: rett etter et søk er utsnittet per
   * definisjon like stort som `viewKm`, så ingen terskel slår ut og knappen
   * forsvinner. Måler man mot den kappede radien i stedet, er utsnittet ved lav
   * zoom permanent større enn radien — og knappen ville stått låst synlig.
   */
  viewKm: number;
}

/** Storsirkelavstand i km. */
export const haversineKm = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(a)));
};

export const boundsCenter = (b: MapBounds): { lat: number; lon: number } => ({
  lat: (b.latMin + b.latMax) / 2,
  lon: (b.lonMin + b.lonMax) / 2,
});

/**
 * Sirkelen vi søker i for et gitt utsnitt.
 *
 * Radien dekker utsnittets hjørne (halve diagonalen) slik at det brukeren SER blir
 * søkt — men aldri mer enn taket. Ved høy zoom blir den derfor liten og billig; ved
 * lav zoom kappes den, og da er det synlig for brukeren at bare midten er søkt.
 */
/** Avstand fra sentrum til det FJERNESTE hjørnet. */
export const maxCornerKm = (b: MapBounds): number => {
  const c = boundsCenter(b);
  return Math.max(
    haversineKm(c.lat, c.lon, b.latMin, b.lonMin),
    haversineKm(c.lat, c.lon, b.latMin, b.lonMax),
    haversineKm(c.lat, c.lon, b.latMax, b.lonMin),
    haversineKm(c.lat, c.lon, b.latMax, b.lonMax),
  );
};

export const searchCircleForBounds = (b: MapBounds): SearchCircle => {
  const c = boundsCenter(b);
  // MAKS over alle fire hjørner, ikke bare ett: på en kule er de ikke like langt
  // unna, fordi lengdegrader krymper mot polene.
  const viewKm = maxCornerKm(b);
  return { lat: c.lat, lon: c.lon, radiusKm: radiusForViewKm(viewKm), viewKm };
};

/**
 * Skal vi tilby «Søk i dette området»?
 *
 * Begge grunnene måles mot HVA VI FAKTISK HENTET (`radiusKm`), ikke mot hvor stort
 * utsnittet tilfeldigvis var. Det er dét som gir brukeren følelsen av kontroll:
 * knappen dukker opp når du beveger deg vekk fra det søkte området, ikke når du
 * flytter deg en halv skjermbredde.
 *
 *  1. FLYTTET DEG BORT: sentrum har flyttet seg mer enn halve radien. Med gulvet
 *     på 25 km er det minst 12,5 km — du må faktisk forlate området.
 *
 *  2. ET NYTT SØK VILLE GITT MER: zoomer du ut, ville et nytt søk fått en større
 *     radius. Det er dette som er den ærlige zoom-testen, og den kan aldri låse
 *     knappen synlig: rett etter et søk er `nyRadius === c.radiusKm`, så
 *     terskelen slår ikke ut. Ved taket (50 km) slutter den å slå ut helt, fordi
 *     et nytt søk da ikke ville gitt én meter mer.
 *
 * Merk at grunn 2 IKKE betyr «hent automatisk ved zoom». Den sier bare at knappen
 * skal finnes. Porten i useSpotManager henter kun når APPEN flyttet kartet
 * (`utils/mapMoveOrigin`); brukerens egen zoom lander alltid på knappen.
 */
export const needsNewSearch = (b: MapBounds, c: SearchCircle | null): boolean => {
  if (!c) return true;
  const senter = boundsCenter(b);
  if (haversineKm(senter.lat, senter.lon, c.lat, c.lon) > c.radiusKm * RESEARCH_MOVE_FRACTION) return true;
  return radiusForViewKm(maxCornerKm(b)) > c.radiusKm * RESEARCH_GROWTH_FACTOR;
};

/** Bbox som omslutter sirkelen — det vi faktisk spør Firestore om. */
export const boundsForCircle = (c: SearchCircle): MapBounds => {
  const dLat = (c.radiusKm / EARTH_RADIUS_KM) * (180 / Math.PI);
  const cosLat = Math.max(0.01, Math.cos(toRad(c.lat)));
  const dLon = dLat / cosLat;
  return {
    latMin: Math.max(-90, c.lat - dLat),
    latMax: Math.min(90, c.lat + dLat),
    lonMin: Math.max(-180, c.lon - dLon),
    lonMax: Math.min(180, c.lon + dLon),
  } as MapBounds;
};

export const isInsideCircle = (lat: number, lon: number, c: SearchCircle): boolean =>
  haversineKm(c.lat, c.lon, lat, lon) <= c.radiusKm;

/**
 * Er hele utsnittet dekket av et tidligere søk?
 *
 * Alle fire hjørner må ligge innenfor. Er ett utenfor, finnes det synlig område vi
 * ikke har søkt i — og da skal «Søk i dette området» tilbys.
 */
export const isBoundsCoveredByCircle = (b: MapBounds, c: SearchCircle | null): boolean => {
  if (!c) return false;
  return isInsideCircle(b.latMin, b.lonMin, c)
    && isInsideCircle(b.latMin, b.lonMax, c)
    && isInsideCircle(b.latMax, b.lonMin, c)
    && isInsideCircle(b.latMax, b.lonMax, c);
};
